import { WorkflowsApi } from "../../apiClient";
import type {
    ActionRuleWrite,
    ConditionTreeResponse,
    ConditionWrite,
    DryRunResponse,
    EditorMetadata,
    LinkingRule,
    PaginatedWorkflowDefListList,
    TransitionActionWrite,
    TransitionWrite,
    WorkflowDefCreate,
    WorkflowDefWrite,
} from "../../apiClient/generated";
import { ResponseError } from "../../apiClient/generated";
import type { TransitionType, WorkflowDefinition } from "./types";

/**
 * API client for the workflow editor.
 *
 * The transport is the generated client — these endpoints are in the OpenAPI schema, so the
 * request and response types below come from the serializers rather than being restated here.
 * What this module still owns is error translation.
 *
 * The generated runtime throws a single ResponseError carrying the raw Response for anything
 * non-2xx, which is not enough for the UI: a stale condition and a rejected field need to be told
 * apart and shown differently.
 */

/** Raised when the server rejected a write because the caller's copy was stale. */
export class StaleWriteError extends Error {
    constructor(detail: string) {
        super(detail);
        this.name = "StaleWriteError";
    }
}

/**
 * A rejected write, with the server's field errors intact.
 *
 * Keeping the structure lets the UI put each message on the field that caused it, rather than
 * rendering the whole body into a red box.
 */
export class ApiValidationError extends Error {
    readonly fieldErrors: Record<string, string[]>;
    readonly warnings: string[];
    readonly acknowledgementRequired: boolean;

    constructor(body: unknown) {
        const { fieldErrors, warnings, acknowledgementRequired } =
            parseErrorBody(body);
        // join() returns "" rather than anything nullish, so the fallback has to be reached
        // with || — a ?? chain leaves an empty body producing an error with no message at all.
        const summary =
            fieldErrors.detail?.[0] ??
            Object.entries(fieldErrors)
                .map(([field, messages]) => `${field}: ${messages.join(" ")}`)
                .join("; ");
        super(summary || "The server rejected this change.");
        this.name = "ApiValidationError";
        this.fieldErrors = fieldErrors;
        this.warnings = warnings;
        this.acknowledgementRequired = acknowledgementRequired;
    }
}

/** Flatten a DRF error body into field → messages, keeping nested keys addressable. */
function parseErrorBody(body: unknown): {
    fieldErrors: Record<string, string[]>;
    warnings: string[];
    acknowledgementRequired: boolean;
} {
    const fieldErrors: Record<string, string[]> = {};
    let warnings: string[] = [];
    let acknowledgementRequired = false;

    if (typeof body !== "object" || body === null) {
        return { fieldErrors, warnings, acknowledgementRequired };
    }

    for (const [key, value] of Object.entries(
        body as Record<string, unknown>,
    )) {
        if (key === "warnings" && Array.isArray(value)) {
            warnings = value.map(String);
            continue;
        }
        if (key === "acknowledgement_required") {
            acknowledgementRequired = Boolean(value);
            continue;
        }
        if (Array.isArray(value)) {
            fieldErrors[key] = value.map(String);
        } else if (typeof value === "object" && value !== null) {
            // Nested, e.g. {"extra_params": {"responsible": ["..."]}} — flattened to a dotted
            // path so the UI can address the exact input.
            for (const [nestedKey, nestedValue] of Object.entries(
                value as Record<string, unknown>,
            )) {
                fieldErrors[`${key}.${nestedKey}`] = Array.isArray(nestedValue)
                    ? nestedValue.map(String)
                    : [String(nestedValue)];
            }
        } else {
            fieldErrors[key] = [String(value)];
        }
    }

    return { fieldErrors, warnings, acknowledgementRequired };
}

/**
 * Run a generated-client call, translating its ResponseError into the editor's own.
 *
 * Every call in this module goes through here. Error bodies are read raw rather than through a
 * generated model: DRF error shapes are
 * not in the schema, and field errors are keyed by the serializer's own snake_case field names,
 * which is what the UI matches against.
 */
async function translate<T>(call: () => Promise<T>): Promise<T> {
    try {
        return await call();
    } catch (error) {
        if (!(error instanceof ResponseError)) {
            throw error;
        }

        // 401 is not handled here: the shared client's middleware redirects to the login page,
        // as it does for every other screen.
        const { status } = error.response;
        const body = await error.response.json().catch(() => ({}));

        if (status === 409) {
            throw new StaleWriteError(
                typeof body.detail === "string"
                    ? body.detail
                    : "This changed since it was loaded. Reload and reapply your changes.",
            );
        }

        throw new ApiValidationError(body);
    }
}

const api = () => WorkflowsApi();

export const queryKeys = {
    definition: (id: string) => ["workflow-definition", id] as const,
    metadata: (id: string) => ["workflow-definition-metadata", id] as const,
};

/* --- definitions ---------------------------------------------------------- */

/** Params as DataTableV3 supplies them. */
export interface DefinitionPageParams {
    page?: number;
    pageSize?: number;
    ordering?: string;
}

export function fetchDefinitions(
    params: DefinitionPageParams,
): Promise<PaginatedWorkflowDefListList> {
    return translate(() => api().adminWorkflowDefinitionsList(params));
}

export interface DefinitionInput {
    name: string;
    description?: string;
    /** Omitted or null creates a global template, which belongs to no company. */
    owner?: number | null;
    isActive?: boolean;
    autoEventLinking?: boolean;
}

/**
 * Create a definition.
 *
 * ``owner`` is writable here and nowhere else — reassigning it later would invalidate every
 * reference param configured on the definition without saying so.
 */
export function createDefinition(
    input: DefinitionInput,
): Promise<WorkflowDefCreate> {
    return translate(() =>
        api().adminWorkflowDefinitionsCreate({
            workflowDefCreateRequest: input,
        }),
    );
}

export function fetchDefinition(id: string): Promise<WorkflowDefinition> {
    return translate(() => api().adminWorkflowDefinitionsRetrieve({ id }));
}

export function fetchMetadata(id: string): Promise<EditorMetadata> {
    return translate(() =>
        api().adminWorkflowDefinitionsMetadataRetrieve({ id }),
    );
}

export interface DefinitionFields {
    name: string;
    description: string;
    isActive: boolean;
    autoEventLinking: boolean;
}

export function patchDefinition(
    id: string,
    fields: Partial<DefinitionFields>,
): Promise<WorkflowDefWrite> {
    return translate(() =>
        api().adminWorkflowDefinitionsPartialUpdate({
            id,
            patchedWorkflowDefWriteRequest: fields,
        }),
    );
}

/* ---------------------------------------------------------------------------
 * Writes
 *
 * Positions are never sent. The server assigns them on create and the reorder endpoints take
 * an ordered list of ids — rule positions are unique per action at the database level, so a
 * client-chosen number would collide with an existing row.
 * ------------------------------------------------------------------------- */

export interface TransitionInput {
    workflow: string;
    fromStatus: string;
    toStatus: string;
    transitionType: TransitionType;
    priority?: number;
}

export function createTransition(
    input: TransitionInput,
): Promise<TransitionWrite> {
    return translate(() =>
        api().adminWorkflowTransitionsCreate({
            transitionWriteRequest: input as never,
        }),
    );
}

export function patchTransition(
    id: string,
    fields: Partial<Omit<TransitionInput, "workflow">>,
): Promise<TransitionWrite> {
    return translate(() =>
        api().adminWorkflowTransitionsPartialUpdate({
            id,
            patchedTransitionWriteRequest: fields as never,
        }),
    );
}

export function deleteTransition(id: string): Promise<void> {
    return translate(() => api().adminWorkflowTransitionsDestroy({ id }));
}

/* --- configured actions --------------------------------------------------- */

export function createAction(
    transitionId: string,
    actionName: string,
): Promise<TransitionActionWrite> {
    return translate(() =>
        api().adminWorkflowTransitionActionsCreate({
            transitionActionWriteRequest: {
                transition: transitionId,
                actionName,
            },
        }),
    );
}

export function deleteAction(id: string): Promise<void> {
    return translate(() => api().adminWorkflowTransitionActionsDestroy({ id }));
}

export function reorderActions(
    transitionId: string,
    actionIds: string[],
): Promise<unknown> {
    return translate(() =>
        api().adminWorkflowTransitionActionsReorderCreate({
            actionReorderRequest: { transition: transitionId, actionIds },
        }),
    );
}

/* --- rules ---------------------------------------------------------------- */

export function createRule(
    transitionActionId: string,
): Promise<ActionRuleWrite> {
    return translate(() =>
        api().adminWorkflowActionRulesCreate({
            actionRuleWriteRequest: {
                transitionAction: transitionActionId,
                extraParams: {},
            },
        }),
    );
}

export function patchRuleParams(
    id: string,
    extraParams: Record<string, unknown>,
): Promise<ActionRuleWrite> {
    return translate(() =>
        api().adminWorkflowActionRulesPartialUpdate({
            id,
            patchedActionRuleWriteRequest: { extraParams },
        }),
    );
}

export function deleteRule(id: string): Promise<void> {
    return translate(() => api().adminWorkflowActionRulesDestroy({ id }));
}

export function reorderRules(
    transitionActionId: string,
    ruleIds: string[],
): Promise<unknown> {
    return translate(() =>
        api().adminWorkflowActionRulesReorderCreate({
            ruleReorderRequest: {
                transitionAction: transitionActionId,
                ruleIds,
            },
        }),
    );
}

/* --- conditions ---------------------------------------------------------- */

export function createCondition(
    workflowId: string,
    name: string,
): Promise<ConditionWrite> {
    return translate(() =>
        api().adminWorkflowConditionsCreate({
            conditionWriteRequest: { workflow: workflowId, name },
        }),
    );
}

export function attachConditionToTransition(
    transitionId: string,
    conditionId: string,
): Promise<TransitionWrite> {
    return translate(() =>
        api().adminWorkflowTransitionsPartialUpdate({
            id: transitionId,
            patchedTransitionWriteRequest: { condition: conditionId },
        }),
    );
}

export function attachConditionToRule(
    ruleId: string,
    conditionId: string,
): Promise<ActionRuleWrite> {
    return translate(() =>
        api().adminWorkflowActionRulesPartialUpdate({
            id: ruleId,
            patchedActionRuleWriteRequest: { condition: conditionId },
        }),
    );
}

export interface ConditionTreeInput {
    groups: unknown[];
    /**
     * Optimistic-concurrency token, echoed verbatim from the ``version`` the read handed out.
     *
     * A string, deliberately. It was a datetime, which the generated client parsed into a JS
     * Date — millisecond precision against the microseconds Postgres stores — so the token sent
     * back never matched the one stored and every save came back as a false conflict.
     */
    expectedVersion: string;
    acknowledgeWarnings: boolean;
}

export function putConditionTree(
    conditionId: string,
    input: ConditionTreeInput,
): Promise<ConditionTreeResponse> {
    return translate(() =>
        api().adminWorkflowConditionsTreeUpdate({
            id: conditionId,
            conditionTreeWriteRequest: {
                groups: input.groups,
                expectedVersion: input.expectedVersion,
                acknowledgeWarnings: input.acknowledgeWarnings,
            },
        }),
    );
}

/**
 * Remove a condition from whatever holds it, and delete it.
 *
 * Not a DELETE: ActionRule.condition cascades, so deleting an attached condition takes the rule
 * with it. The server clears the references and deletes the condition in one transaction.
 */
export function detachCondition(conditionId: string): Promise<unknown> {
    return translate(() =>
        api().adminWorkflowConditionsDetachCreate({ id: conditionId }),
    );
}

/* --- dry run ------------------------------------------------------------- */

export function dryRun(
    definitionId: string,
    eventId: string,
): Promise<DryRunResponse> {
    return translate(() =>
        api().adminWorkflowDefinitionsDryRunCreate({
            id: definitionId,
            dryRunRequest: { event: eventId },
        }),
    );
}

/* --- linking rule --------------------------------------------------------- */

export interface LinkingRuleFields {
    dataProvider: string | null;
    groupingFields: string[];
    mainStrategy: LinkingRule["mainStrategy"];
}

export function createLinkingRule(
    workflowId: string,
    fields: LinkingRuleFields,
): Promise<LinkingRule> {
    return translate(() =>
        api().adminWorkflowLinkingRulesCreate({
            linkingRuleRequest: { workflow: workflowId, ...fields },
        }),
    );
}

export function deleteLinkingRule(linkingRuleId: number): Promise<void> {
    return translate(() =>
        api().adminWorkflowLinkingRulesDestroy({ id: linkingRuleId }),
    );
}

export function patchLinkingRule(
    linkingRuleId: number,
    fields: Partial<LinkingRuleFields>,
): Promise<LinkingRule> {
    // Addressed by the rule's own pk, not the workflow's: a definition can have several linking
    // rules (one per data provider), each with an AutoField primary key of its own.
    return translate(() =>
        api().adminWorkflowLinkingRulesPartialUpdate({
            id: linkingRuleId,
            patchedLinkingRuleRequest: fields,
        }),
    );
}
